import { useState } from 'react';
import { useMapElement } from '../contextProviders/MapElementProvider';
import NameInput from '../common/NameInput';
import OutcomeList from '../common/outcomes/OutcomeList';
import ModalBody from '../../../generic/modal/ModalBody';
import ModalFooter from '../../../generic/modal/ModalFooter';
import { isNullOrWhitespace } from '../../../../utils/guard';
import type { MapElementRequestAPI } from '../../../../sources/graph';
import Footer from '../common/Footer';
import translations from '../../../../translations';
import AccountDropdown from '../../../generic/AccountDropdown';
import type { Agent, AgentType } from '../../../../types/graph';
import FormGroup from '../../../generic/FormGroup';
import AgentDropdown from '../../../generic/AgentDropdown';
import AgentMessage from './AgentMessage';
import AgentSessionId from './AgentSessionId';
import AgentOutput from './AgentOutput';
import { SYSTEM_TYPE_NAME_ID_MAPPINGS } from '../../../../constants/system';

const chatMessageTypeElementId = SYSTEM_TYPE_NAME_ID_MAPPINGS['$Chat Message'];

const AgentConfiguration = () => {
    const { mapElement, onSaveMapElement, onClose, onUpdateName, setMapElement, container } =
        useMapElement();

    const [agent, setAgent] = useState<Agent>(
        mapElement.agent || {
            accountId: '',
            agentId: null,
            agentMode: 'Conversational',
            message: null,
            output: null,
            sessionId: null,
            type: 'StartConversation',
        },
    );

    const [hasSubmitted, setHasSubmitted] = useState(false);

    const onChange = (value: Partial<Agent>) => {
        setAgent({
            ...agent,
            ...value,
        });
    };

    const isFormValid = () => {
        if (isNullOrWhitespace(mapElement?.developerName) || !agent.accountId) {
            return false;
        }

        switch (agent.type) {
            case 'AddContext':
                return agent.agentId && agent.sessionId && agent.message;

            case 'GetConversation':
                return agent.sessionId && agent.output;

            case 'StartConversation':
                return agent.agentId && agent.message;

            case 'Structured':
                return agent.agentId && agent.message && agent.output;
        }
    };

    const onSave = () => {
        setHasSubmitted(true);

        if (isFormValid()) {
            onSaveMapElement({
                ...mapElement,
                agent,
            } as MapElementRequestAPI);
        }
    };

    if (!mapElement) {
        onClose();
        return;
    }

    const configProps = {
        container,
        hasSubmitted,
        onChange,
    };

    const renderBody = () => (
        <>
            <NameInput
                isValid={!!mapElement.developerName}
                showValidation={hasSubmitted}
                id="agent-name"
                name={mapElement.developerName ?? ''}
                onUpdateName={onUpdateName}
            />
            <AccountDropdown
                label={translations.AGENT_account_label}
                isRequired={true}
                showValidation={hasSubmitted}
                validationMessage={translations.AGENT_account_required}
                value={agent?.accountId || ''}
                onChange={(accountId) => onChange({ accountId: accountId || '' })}
                className="form-control-long"
            />
            <AgentDropdown
                accountId={agent?.accountId}
                label={translations.AGENT_agent_label}
                isRequired={true}
                showValidation={hasSubmitted}
                validationMessage={translations.AGENT_agent_required}
                value={agent?.agentId || null}
                onChange={(agent) =>
                    onChange({
                        agentId: agent?.id || '',
                        agentMode: agent?.agentMode || 'Conversational',
                        type:
                            agent?.agentMode === 'Structured' ? 'Structured' : 'StartConversation',
                    })
                }
                className="form-control-long"
            />
            {agent?.agentMode === 'Conversational' ? (
                <FormGroup
                    label={translations.AGENT_type_label}
                    isRequired={true}
                    isValid={!!agent}
                    showValidation={hasSubmitted}
                    validationMessage={translations.AGENT_type_required}
                    htmlFor="agent-chat-type"
                >
                    <select
                        id="agent-chat-type"
                        className="form-control form-control-dynamic"
                        value={agent?.type}
                        onChange={(e) => onChange({ type: e.currentTarget.value as AgentType })}
                    >
                        <option value="StartConversation">
                            {translations.AGENT_start_conversation_label}
                        </option>
                        <option value="GetConversation">
                            {translations.AGENT_get_conversation_label}
                        </option>
                        <option value="AddContext">{translations.AGENT_add_context_label}</option>
                    </select>
                </FormGroup>
            ) : null}
            {agent.type === 'Structured' && (
                <>
                    <AgentMessage
                        {...configProps}
                        value={agent?.message}
                        label={translations.AGENT_input_label}
                        validationMessage={translations.AGENT_input_required}
                    />
                    <AgentOutput
                        {...configProps}
                        value={agent?.output}
                        isRequired={true}
                        label={translations.AGENT_output_label}
                        validationMessage={translations.AGENT_output_required}
                    />
                </>
            )}
            {agent.type === 'StartConversation' && (
                <>
                    <AgentMessage {...configProps} value={agent?.message} />
                    <AgentSessionId {...configProps} isRequired={false} value={agent?.sessionId} />
                    <AgentOutput {...configProps} isRequired={false} value={agent?.output} />
                </>
            )}
            {agent.type === 'GetConversation' && (
                <>
                    <AgentSessionId {...configProps} isRequired={true} value={agent?.sessionId} />
                    <AgentOutput
                        {...configProps}
                        isRequired={true}
                        value={agent?.output}
                        label={translations.AGENT_messages_label}
                        validationMessage={translations.AGENT_messages_required}
                        contentType="ContentList"
                        typeElementId={chatMessageTypeElementId}
                    />
                </>
            )}
            {agent.type === 'AddContext' && (
                <>
                    <AgentSessionId {...configProps} isRequired={true} value={agent?.sessionId} />
                    <AgentMessage {...configProps} value={agent?.message} />
                    <AgentOutput {...configProps} isRequired={false} value={agent?.output} />
                </>
            )}
            <OutcomeList />
            <FormGroup
                key="agent-comments"
                label={translations.AGENT_comments_label}
                htmlFor="agent-comments"
            >
                <textarea
                    id="agent-comments"
                    data-testid="agent-comments"
                    className="form-control form-textarea"
                    value={mapElement.developerSummary ?? ''}
                    onChange={({ target: { value } }) => {
                        setMapElement({
                            ...mapElement,
                            developerSummary: value,
                        });
                    }}
                />
            </FormGroup>
        </>
    );

    const renderFooter = () => (
        <>
            <Footer save={onSave} cancel={onClose} />
        </>
    );

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </>
    );
};

export default AgentConfiguration;
